import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { RatingApi } from "../../api/RatingApi";
import type { ApClassTypeFetchOptions } from "../../generated/dto/ApClassTypeFetchOptions";
import type { ApClassTypeStaff } from "../../generated/dto/ApClassTypeStaff";
import type { CreateRating } from "../../generated/dto/CreateRating";
import type { CreateRatingProgram } from "../../generated/dto/CreateRatingProgram";
import type { DeleteRatingProgramStaff } from "../../generated/dto/DeleteRatingProgramStaff";
import type { DeleteRatingStaff } from "../../generated/dto/DeleteRatingStaff";
import type { JpClassTypeFetchOptions } from "../../generated/dto/JpClassTypeFetchOptions";
import type { JpClassTypeStaff } from "../../generated/dto/JpClassTypeStaff";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonRatingFetchOptions } from "../../generated/dto/PersonRatingFetchOptions";
import type { PersonRatingGrid } from "../../generated/dto/PersonRatingGrid";
import type { PersonRatingSearchParams } from "../../generated/dto/PersonRatingSearchParams";
import type { PersonRatingStaff } from "../../generated/dto/PersonRatingStaff";
import type { PersonRatingStaffSearchResult } from "../../generated/dto/PersonRatingStaffSearchResult";
import type { PersonRef } from "../../generated/dto/PersonRef";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { ProgramTypeStaff } from "../../generated/dto/ProgramTypeStaff";
import type { RatingFetchOptions } from "../../generated/dto/RatingFetchOptions";
import type { RatingProgramFetchOptions } from "../../generated/dto/RatingProgramFetchOptions";
import type { RatingProgramStaff } from "../../generated/dto/RatingProgramStaff";
import type { RatingSearchParams } from "../../generated/dto/RatingSearchParams";
import type { RatingStaff } from "../../generated/dto/RatingStaff";
import type { RatingStaffSearchResult } from "../../generated/dto/RatingStaffSearchResult";
import type { UpdateRating } from "../../generated/dto/UpdateRating";
import type { UpdateRatingProgram } from "../../generated/dto/UpdateRatingProgram";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class RatingApiClient implements RatingApi {
  constructor(private readonly client = useClient()) {}

  async getRatingStaff(id: number, options?: RatingFetchOptions): Promise<RatingStaff> {
    const { data } = await this.client.get<unknown>(`/rating/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff>(data, "generated.dto.RatingStaff");
  }

  async getRatingProgramStaff(id: number, options?: RatingProgramFetchOptions): Promise<RatingProgramStaff> {
    const { data } = await this.client.get<unknown>(`/ratingprogram/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.RatingProgramFetchOptions") });
    return deserializeCbiDto<RatingProgramStaff>(data, "generated.dto.RatingProgramStaff");
  }

  async getPersonRatingStaff(id: number, options?: PersonRatingFetchOptions): Promise<PersonRatingStaff> {
    const { data } = await this.client.get<unknown>(`/personrating/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonRatingFetchOptions") });
    return deserializeCbiDto<PersonRatingStaff>(data, "generated.dto.PersonRatingStaff");
  }

  async getAllRatingStaffs(options?: RatingFetchOptions): Promise<Array<RatingStaff>> {
    const { data } = await this.client.get<unknown>(`/rating/staff/all`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<Array<RatingStaff>>(data, "generated.dto.RatingStaff");
  }

  async getAllRatingProgramStaffs(options?: RatingProgramFetchOptions): Promise<Array<RatingProgramStaff>> {
    const { data } = await this.client.get<unknown>(`/ratingprogram/staff/all`, { params: serializeCbiDto(options, "generated.dto.RatingProgramFetchOptions") });
    return deserializeCbiDto<Array<RatingProgramStaff>>(data, "generated.dto.RatingProgramStaff");
  }

  async createRatingStaff(source: CreateRating): Promise<RatingStaff> {
    const { data } = await this.client.post<unknown>(`/rating/staff`, serializeCbiDto(source, "generated.dto.CreateRating"));
    return deserializeCbiDto<RatingStaff>(data, "generated.dto.RatingStaff");
  }

  async createRatingProgramStaff(source: CreateRatingProgram): Promise<RatingProgramStaff> {
    const { data } = await this.client.post<unknown>(`/ratingprogram/staff`, serializeCbiDto(source, "generated.dto.CreateRatingProgram"));
    return deserializeCbiDto<RatingProgramStaff>(data, "generated.dto.RatingProgramStaff");
  }

  async updateRatingStaff(source: UpdateRating): Promise<RatingStaff> {
    const { data } = await this.client.put<unknown>(`/rating/staff/${source.ratingId}`, serializeCbiDto(source, "generated.dto.UpdateRating"));
    return deserializeCbiDto<RatingStaff>(data, "generated.dto.RatingStaff");
  }

  async updateRatingProgramStaff(source: UpdateRatingProgram): Promise<RatingProgramStaff> {
    const { data } = await this.client.put<unknown>(`/ratingprogram/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateRatingProgram"));
    return deserializeCbiDto<RatingProgramStaff>(data, "generated.dto.RatingProgramStaff");
  }

  async deleteRatingStaff(source: DeleteRatingStaff): Promise<void> {
    await this.client.delete(`/rating/staff/${source.ratingId}`);
  }

  async deleteRatingProgramStaff(source: DeleteRatingProgramStaff): Promise<void> {
    await this.client.delete(`/ratingprogram/staff/${source.assignId}`);
  }

  async searchRatingStaff(params: RatingSearchParams, page: number, perPage: number): Promise<PaginationResult<RatingStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/rating/staff/search`, serializeCbiDto(params, "generated.dto.RatingSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<RatingStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<RatingStaffSearchResult>>(result.items, "generated.dto.RatingStaffSearchResult") };
    return result;
  }

  async searchPersonRatingStaff(params: PersonRatingSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonRatingStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personrating/staff/search`, serializeCbiDto(params, "generated.dto.PersonRatingSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonRatingStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonRatingStaffSearchResult>>(result.items, "generated.dto.PersonRatingStaffSearchResult") };
    return result;
  }

  async searchRatingStaffFull(params: RatingSearchParams, page: number, perPage: number, options?: RatingFetchOptions): Promise<PaginationResult<RatingStaff>> {
    const { data } = await this.client.post<unknown>(`/rating/staff/search/full`, serializeCbiDto(params, "generated.dto.RatingSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.RatingFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<RatingStaff>;
    result = { ...result, items: deserializeCbiDto<Array<RatingStaff>>(result.items, "generated.dto.RatingStaff") };
    return result;
  }

  async searchPersonRatingStaffFull(params: PersonRatingSearchParams, page: number, perPage: number, options?: PersonRatingFetchOptions): Promise<PaginationResult<PersonRatingStaff>> {
    const { data } = await this.client.post<unknown>(`/personrating/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonRatingSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonRatingFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonRatingStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonRatingStaff>>(result.items, "generated.dto.PersonRatingStaff") };
    return result;
  }

  async getRatingStaffOverriddenBy(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/rating/staff/${id}/overriddenby`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getRatingStaffTypesRequiringThisRatingAsPrereq(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/rating/staff/${id}/typesrequiringthisratingasprereq`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<Array<ApClassTypeStaff>>(data, "generated.dto.ApClassTypeStaff");
  }

  async getRatingStaffTypesRequiringThisRatingAsOverkill(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/rating/staff/${id}/typesrequiringthisratingasoverkill`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<Array<ApClassTypeStaff>>(data, "generated.dto.ApClassTypeStaff");
  }

  async getRatingStaffTypesTeachableWithThisRating(id: number, options?: ApClassTypeFetchOptions): Promise<Array<ApClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/rating/staff/${id}/typesteachablewiththisrating`, { params: serializeCbiDto(options, "generated.dto.ApClassTypeFetchOptions") });
    return deserializeCbiDto<Array<ApClassTypeStaff>>(data, "generated.dto.ApClassTypeStaff");
  }

  async getRatingStaffJpTypesRequiringThisRatingAsPrereq(id: number, options?: JpClassTypeFetchOptions): Promise<Array<JpClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/rating/staff/${id}/jptypesrequiringthisratingasprereq`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<Array<JpClassTypeStaff>>(data, "generated.dto.JpClassTypeStaff");
  }

  async getRatingStaffJpTypesRequiringThisRatingAsOverkill(id: number, options?: JpClassTypeFetchOptions): Promise<Array<JpClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/rating/staff/${id}/jptypesrequiringthisratingasoverkill`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<Array<JpClassTypeStaff>>(data, "generated.dto.JpClassTypeStaff");
  }

  async getRatingStaffJpTypesTeachableWithThisRating(id: number, options?: JpClassTypeFetchOptions): Promise<Array<JpClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/rating/staff/${id}/jptypesteachablewiththisrating`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<Array<JpClassTypeStaff>>(data, "generated.dto.JpClassTypeStaff");
  }

  async getRatingProgramStaffRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/ratingprogram/staff/${id}/ratingid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getRatingProgramStaffProgramId(id: number): Promise<ProgramTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/ratingprogram/staff/${id}/programid`);
    return deserializeCbiDto<ProgramTypeStaff | null>(data, "generated.dto.ProgramTypeStaff");
  }

  async getPersonRatingStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personrating/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getPersonRatingStaffRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff> {
    const { data } = await this.client.get<unknown>(`/personrating/staff/${id}/ratingid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff>(data, "generated.dto.RatingStaff");
  }

  async getPersonRatingStaffProgramId(id: number): Promise<ProgramTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/personrating/staff/${id}/programid`);
    return deserializeCbiDto<ProgramTypeStaff | null>(data, "generated.dto.ProgramTypeStaff");
  }

  async getPersonRatingGridStaff(person: PersonRef): Promise<PersonRatingGrid> {
    const { data } = await this.client.post<unknown>(`/rating/staff/getpersonratinggrid`, serializeCbiDto(person, "generated.dto.PersonRef"));
    return deserializeCbiDto<PersonRatingGrid>(data, "generated.dto.PersonRatingGrid");
  }

  async getPersonRatingGridMember(person: PersonRef): Promise<PersonRatingGrid> {
    const { data } = await this.client.post<unknown>(`/rating/member/getpersonratinggrid`, serializeCbiDto(person, "generated.dto.PersonRef"));
    return deserializeCbiDto<PersonRatingGrid>(data, "generated.dto.PersonRatingGrid");
  }
}

export const useRatingApi = (): RatingApi => new RatingApiClient();
